/**
 * usePharmacySession.ts — read the PHARMACY session reactively.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY a hook rather than a context provider: the pharmacy console is one route
 * with one shell, and the session is written in exactly two places (sign-in and
 * sign-out/401). A provider would add a wrapper to the tree — and a second thing
 * to remember to mount — for state that is already a module-level store. The
 * subscription is what makes it reactive: a 401 anywhere in the console clears
 * the store and every mounted reader drops to `null` in the same tick, so the
 * route gate redirects instead of leaving a stale console on screen.
 *
 * Deliberately NOT folded into `auth/AuthContext`: that context hydrates from
 * `GET /auth/me` and drives `/auth` redirects for patients and staff. A
 * pharmacist has neither. See pharmacy/session.ts for the full reasoning.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  getPharmacySession,
  notifyPharmacyUnauthorized,
  onPharmacyUnauthorized,
  type PharmacySession,
} from './session';

export interface PharmacySessionState {
  session: PharmacySession | null;
  /** Clear the session everywhere (the route gate then routes to sign-in). */
  signOut: () => void;
}

export function usePharmacySession(): PharmacySessionState {
  const [session, setSession] = useState<PharmacySession | null>(getPharmacySession);

  // One subscription per reader: a 401 (or another component signing out) drops
  // them all together, so no part of the console keeps rendering against a dead
  // session.
  useEffect(() => onPharmacyUnauthorized(() => setSession(null)), []);

  const signOut = useCallback(() => {
    notifyPharmacyUnauthorized();
  }, []);

  return { session, signOut };
}
